// Hook centralisant les variantes perso de l'utilisateur : le catalogue
// (nom/abréviation/couleur) + les applications (sur une carte ou un set) +
// les actions create/update/delete/apply. Calqué sur useCustomLists.js.
//
// Note : l'AFFICHAGE des variantes perso sur les cartes ne passe PAS par ce
// hook — le backend attache déjà card.custom_variants aux cartes servies.
// Ce hook sert au catalogue (écran de gestion) et aux sélecteurs d'application.

import { useState, useCallback, useEffect } from "react";
import {
  fetchCustomVariants, createCustomVariant, updateCustomVariant,
  deleteCustomVariant, applyCustomVariant, removeCustomVariantApplication,
} from "../lib/api";

export function useCustomVariants() {
  const [variants, setVariants] = useState([]);
  const [applications, setApplications] = useState([]);

  // Un échec ne doit pas passer pour « aucune variante » : deux essais, puis la
  // cause est exposée aux écrans.
  const [loadError, setLoadError] = useState(null);

  const reload = useCallback(async () => {
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const { variants: v, applications: a } = await fetchCustomVariants();
        setVariants(v);
        setApplications(a);
        setLoadError(null);
        return;
      } catch (err) {
        console.error(err);
        if (attempt === 2) setLoadError(err?.response?.status ? `erreur ${err.response.status}` : "pas de réponse du serveur");
      }
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const addVariant = useCallback(async ({ name, abbr, color }) => {
    try {
      const created = await createCustomVariant({ name, abbr, color });
      setVariants(prev => [...prev, created]);
      return created;
    } catch (err) { console.error(err); return null; }
  }, []);

  const editVariant = useCallback(async (variantId, patch) => {
    try {
      await updateCustomVariant(variantId, patch);
      setVariants(prev => prev.map(v => v.id === variantId ? { ...v, ...patch } : v));
    } catch (err) { console.error(err); }
  }, []);

  const removeVariant = useCallback(async (variantId) => {
    try {
      await deleteCustomVariant(variantId);
      setVariants(prev => prev.filter(v => v.id !== variantId));
      setApplications(prev => prev.filter(a => a.variant_id !== variantId));
    } catch (err) { console.error(err); }
  }, []);

  // Applique une variante à une carte ou à un set (rareté optionnelle).
  // Recharge les applications pour refléter l'état exact renvoyé par le backend.
  const applyVariant = useCallback(async (variantId, target) => {
    try {
      await applyCustomVariant(variantId, target);
      reload();
    } catch (err) { console.error(err); }
  }, [reload]);

  const removeApplication = useCallback(async (appId) => {
    try {
      await removeCustomVariantApplication(appId);
      setApplications(prev => prev.filter(a => a.id !== appId));
    } catch (err) { console.error(err); }
  }, []);

  return {
    variants, applications, reload, loadError,
    addVariant, editVariant, removeVariant, applyVariant, removeApplication,
  };
}
